import Request from './request';
import * as query from "@arcgis/core/rest/query";
import Query from "@arcgis/core/rest/support/Query";
import FieldNames from "../defaults/FieldNames.json";
import _ from "lodash";
import GeneralService from './GeneralService';
import { createCountQueryObject, createDistinctQueryObject, createPagedQueryObject, createQueryObject, createQueryObjectForEstimatedTotalCost } from '../utils/QueryHelper';
import { getAllFinalResults } from '../utils/BulkApprovalHelper';
import { getWhereClauseForFeaturesByFundingFields } from '../utils/FeatureHelper';
import { getAppConfig, getAttributeServiceUrl, getNifcToken, getViewServiceUrl } from '../utils/ReduxHelper';
import esriRequest from '@arcgis/core/request';

const FeatureService = {
    getAllFeaturesUntilDone: async function (
        geometry,
        geometryType,
        definitionExpression,
        classType,
        useGeometry = true,
        outfields = '*',
        maxCount = null
    ) {
        try {
            const config = getAppConfig();
            const rootServiceUrl = getViewServiceUrl();
            const geoLayerID =
                geometryType === FieldNames.joinFeatureValues.polygon
                    ? config.ifprsPolygonsView[config.env].layerId
                    : geometryType === FieldNames.joinFeatureValues.point
                      ? config.ifprsPointsView[config.env].layerId
                      : geometryType === FieldNames.joinFeatureValues.line
                        ? config.ifprsLinesView[config.env].layerId
                        : null;
            const url = `${rootServiceUrl}/${geoLayerID}`;

            let allFinalResults = [];
            const numberToTakeEachTime = 2000;
            let startIndex = 0;
            let hasMoreRecords = true;
            let totalFeatureRecords = [];
            let finalResults = '';
            let classFilter = '';

            if (classType === FieldNames.classTypes.treatment) {
                classFilter = `Class = '${FieldNames.classTypes.treatment}'`;
            } else if (classType === FieldNames.classTypes.activity) {
                classFilter = `Class = '${FieldNames.classTypes.activity}'`;
            } else if (classType === FieldNames.classTypes.actualTreatment) {
                classFilter = `Class = '${FieldNames.classTypes.actualTreatment}'`;
            } else if (classType === FieldNames.classTypes.actualActivity) {
                classFilter = `Class = '${FieldNames.classTypes.actualActivity}'`;
            }

            let baseWhere =
                definitionExpression && definitionExpression != null
                    ? classFilter
                        ? `((${definitionExpression}) AND (${classFilter}))`
                        : `(${definitionExpression})`
                    : classFilter
                      ? `(${classFilter})`
                      : `(OBJECTID IS NOT NULL)`;

            let queryObject = createQueryObject(
                useGeometry,
                baseWhere,
                numberToTakeEachTime,
                startIndex,
                geometry,
                outfields,
                maxCount
            );

            while (hasMoreRecords) {
                finalResults = await GeneralService.queryCall(url, queryObject, maxCount);
                if (finalResults && finalResults.length > 0) {
                    allFinalResults.push(finalResults);
                    totalFeatureRecords.push(finalResults);
                    if (!maxCount) {
                        if (finalResults.length === numberToTakeEachTime) {
                            startIndex += numberToTakeEachTime;
                            queryObject.start = startIndex;
                        } else {
                            hasMoreRecords = false;
                        }
                    } else {
                        let tempTotalFeatureRecords = totalFeatureRecords.flat();
                        if (tempTotalFeatureRecords.length < maxCount) {
                            if (finalResults.length === numberToTakeEachTime) {
                                startIndex += numberToTakeEachTime;
                                queryObject.start = startIndex;
                            } else {
                                hasMoreRecords = false;
                            }
                        } else {
                            hasMoreRecords = false;
                        }
                    }
                } else {
                    hasMoreRecords = false;
                }
            }

            const allFinalResultsFlattened =
                allFinalResults != null && allFinalResults.length > 0 ? allFinalResults.flat() : [];
            return allFinalResultsFlattened;
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
    getDistinctValues: async function (
        geometry,
        geometryType,
        baseWhere,
        outFields,
        useGeometry = true
    ) {
        const config = getAppConfig();
        const rootServiceUrl = getViewServiceUrl();
        const geoLayerID =
            geometryType === FieldNames.joinFeatureValues.polygon
                ? config.ifprsPolygonsView[config.env].layerId
                : geometryType === FieldNames.joinFeatureValues.point
                  ? config.ifprsPointsView[config.env].layerId
                  : geometryType === FieldNames.joinFeatureValues.line
                    ? config.ifprsLinesView[config.env].layerId
                    : null;
        const url = `${rootServiceUrl}/${geoLayerID}`;

        let allFinalResults = [];
        let finalResults = '';

        let queryObject = createDistinctQueryObject(useGeometry, baseWhere, geometry, outFields);

        finalResults = await GeneralService.queryCall(url, queryObject).catch((e) => {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        });
        if (finalResults && finalResults.length > 0) {
            let tempTotalFeatureRecords = finalResults.flat();
            allFinalResults.push(tempTotalFeatureRecords);
        }

        const allFinalResultsFlattened =
            allFinalResults != null && allFinalResults.length > 0 ? allFinalResults.flat() : [];
        return allFinalResultsFlattened;
    },

    getWBSIDValues: function () {
        try {
            const config = getAppConfig();
            const rootServiceUrl = getAttributeServiceUrl();
            const url = `${rootServiceUrl}/${config.actionAttributes[config.env].layerId}`;
            let queryObject = new Query({
                where: '(WBSID IS NOT NULL)',
                outFields: 'WBSID',
                orderByFields: 'WBSID',
                returnGeometry: false,
                returnDistinctValues: true,
            });

            return query.executeQueryJSON(url, queryObject).then((results) => {
                let wbsIds = [];
                if (results.features) {
                    wbsIds = results.features.map((eaItem) =>
                        eaItem && eaItem.attributes
                            ? eaItem.attributes[FieldNames.featureFields.wbsID]
                            : ''
                    );
                }
                return wbsIds;
            });
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
    getAllNames: async function () {
        try {
            const config = getAppConfig();
            const rootServiceUrl = getAttributeServiceUrl();
            const url = `${rootServiceUrl}/${config.actionAttributes[config.env].layerId}`;

            let allFinalResults = [];
            const numberToTakeEachTime = 2000;
            let startIndex = 0;
            let hasMoreRecords = true;
            while (hasMoreRecords) {
                const queryObject = new Query({
                    where: "(Name IS NOT NULL AND Name <> '')",
                    outFields: 'Name',
                    orderByFields: 'Name',
                    start: startIndex,
                    num: numberToTakeEachTime,
                    returnDistinctValues: true,
                });
                let interimResults = await GeneralService.queryCall(url, queryObject);
                if (interimResults && interimResults.length > 0) {
                    allFinalResults.push(interimResults);
                    if (interimResults.length === numberToTakeEachTime) {
                        startIndex += numberToTakeEachTime;
                        queryObject.start = startIndex;
                    } else {
                        hasMoreRecords = false;
                    }
                } else {
                    hasMoreRecords = false;
                }
            }

            const allFinalResultsFlattened =
                allFinalResults != null && allFinalResults.length > 0 ? allFinalResults.flat() : [];
            const nameVals = allFinalResultsFlattened.map(
                (eaItem) => eaItem[FieldNames.featureFields.name]
            );
            const distinctNameVals = [...new Set(nameVals)];
            return distinctNameVals;
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },

    getFeatureByObjectID: async function (
        featureObjectID,
        geometryType,
        returnGeometry,
        outFields = '*'
    ) {
        try {
            const config = getAppConfig();
            const rootServiceUrl = getViewServiceUrl();
            const geoLayerID =
                geometryType === FieldNames.joinFeatureValues.polygon
                    ? config.ifprsPolygonsView[config.env].layerId
                    : geometryType === FieldNames.joinFeatureValues.point
                      ? config.ifprsPointsView[config.env].layerId
                      : geometryType === FieldNames.joinFeatureValues.line
                        ? config.ifprsLinesView[config.env].layerId
                        : null;
            const url = `${rootServiceUrl}/${geoLayerID}`;

            try {
                const queryObject = new Query({
                    where: `(${FieldNames.featureFields.objectID}=${featureObjectID})`,
                    outFields: outFields,
                    returnGeometry: returnGeometry,
                });
                return GeneralService.queryCallSingle(url, queryObject);
            } catch (e) {
                throw e;
            }
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
    getConcatenatedFeatureViewByObjectID: async function (
        featureObjectID,
        returnGeometry,
        outFields = '*'
    ) {
        try {
            const config = getAppConfig();
            const rootServiceUrl = getViewServiceUrl();
            //This funding view is the concatenated funding view and it sets the attribute objectID and ID to OBJECTID and ID
            const geoLayerID = config.fundingView[config.env].layerId;
            const url = `${rootServiceUrl}/${geoLayerID}`;

            try {
                const queryObject = new Query({
                    where: `(${FieldNames.featureFields.objectID}=${featureObjectID})`,
                    outFields: outFields,
                    returnGeometry: returnGeometry,
                });
                return GeneralService.queryCallSingle(url, queryObject);
            } catch (e) {
                throw e;
            }
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
    getViewFeatureByJoinValues: async function (
        joinID,
        joinFeature,
        returnGeometry,
        outFields = '*'
    ) {
        try {
            const config = getAppConfig();
            const rootServiceUrl = getViewServiceUrl();
            const geoLayerID = config.fundingView[config.env].layerId;
            const url = `${rootServiceUrl}/${geoLayerID}`;

            try {
                const queryObject = new Query({
                    where: `((${FieldNames.featureFields.joinID} = ${joinID}) AND (${FieldNames.featureFields.joinFeature} = '${joinFeature}'))`,
                    outFields: outFields,
                    returnGeometry: returnGeometry,
                });
                return GeneralService.queryCallSingle(url, queryObject);
            } catch (e) {
                throw e;
            }
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
    getFeaturesByObjectIDs: async function (allFeatureObjectIds) {
        try {
            const config = getAppConfig();
            const rootServiceUrl = getViewServiceUrl();
            let allFinalResults = [];
            const url = `${rootServiceUrl}/${config.fundingView[config.env].layerId}`;
            const numberToTakeEachTime = 100;
            let startCount = 0;
            let hasMoreRecords = true;
            let finalResults = '';
            while (hasMoreRecords) {
                const numberToTake =
                    startCount + numberToTakeEachTime > allFeatureObjectIds.length
                        ? allFeatureObjectIds.length - startCount
                        : numberToTakeEachTime;
                let numbersToRun = allFeatureObjectIds.slice(startCount, numberToTake + startCount);
                try {
                    const whereClause = '(OBJECTID IN (' + numbersToRun.join(', ') + '))';
                    const queryObject = new Query({
                        where: whereClause,
                        outFields: '*',
                    });
                    finalResults = await GeneralService.queryCall(url, queryObject);
                    if (finalResults && finalResults.length > 0) {
                        allFinalResults.push(finalResults);
                    }
                } catch (e) {
                    throw e;
                }
                hasMoreRecords = allFeatureObjectIds.length > startCount + numberToTakeEachTime;
                startCount += numberToTakeEachTime;
            }
            const allFinalResultsFlattened =
                allFinalResults != null && allFinalResults.length > 0 ? allFinalResults.flat() : [];
            return allFinalResultsFlattened;
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
    getLinkedFeaturesByProjectID: function (id) {
        try {
            const config = getAppConfig();
            const rootServiceUrl = getViewServiceUrl();
            const outFields = `${FieldNames.featureFields.objectID}, ${FieldNames.featureFields.name}, 
        ${FieldNames.featureFields.id}, ${FieldNames.featureFields.class}, ${FieldNames.featureFields.entityType}`;
            const queryObject = new Query({
                where: `${FieldNames.featureFields.projectID}=${id}`,
                outFields: outFields,
                returnGeometry: false,
            });
            return query
                .executeQueryJSON(
                    rootServiceUrl + '/' + config.fundingView[config.env].layerId,
                    queryObject
                )
                .then((results) => {
                    let items = [];
                    if (results && results.features && results.features.length > 0) {
                        items = results.features.map((eaItem) => eaItem.attributes);
                    }
                    return items;
                });
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
    getFeaturesByIDs: async function (allFeatureIds) {
        try {
            const config = getAppConfig();
            const rootServiceUrl = getViewServiceUrl();
            let allFinalResults = [];
            const url = `${rootServiceUrl}/${config.fundingView[config.env].layerId}`;

            let whereArray = [];
            allFeatureIds.forEach((id) => {
                whereArray.push('ID = ' + id);
            });

            const numberToTakeEachTime = 100;
            let startCount = 0;
            let hasMoreRecords = true;
            let finalResults = '';
            while (hasMoreRecords) {
                const numberToTake =
                    startCount + numberToTakeEachTime > whereArray.length
                        ? whereArray.length - startCount
                        : numberToTakeEachTime;
                let numbersToRun = whereArray.slice(startCount, numberToTake + startCount);
                try {
                    const whereClause = '(' + numbersToRun.join(' OR ') + ')';
                    const queryObject = new Query({
                        where: whereClause,
                        outFields: '*',
                    });
                    finalResults = await GeneralService.queryCall(url, queryObject);
                    if (finalResults && finalResults.length > 0) {
                        allFinalResults.push(finalResults);
                    }
                } catch (e) {
                    throw e;
                }
                hasMoreRecords = whereArray.length > startCount + numberToTakeEachTime;
                startCount += numberToTakeEachTime;
            }
            const allFinalResultsFlattened =
                allFinalResults != null && allFinalResults.length > 0 ? allFinalResults.flat() : [];
            return allFinalResultsFlattened;
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
    getFeaturesByJoinValues: async function (joinIDList, joinFeature, outFields = '*') {
        try {
            if (!joinIDList || joinIDList.length === 0) {
                return new Promise((resolve, reject) => {
                    resolve([]);
                });
            }

            const config = getAppConfig();
            const rootServiceUrl = getViewServiceUrl();
            const geoLayerID = config.fundingView[config.env].layerId;
            const url = `${rootServiceUrl}/${geoLayerID}`;

            try {
                let allFinalResults = [];
                const numberToTakeEachTime = 200;
                let startCount = 0;
                let hasMoreRecords = true;
                let finalResults = '';
                while (hasMoreRecords) {
                    const numberToTake =
                        startCount + numberToTakeEachTime > joinIDList.length
                            ? joinIDList.length - startCount
                            : numberToTakeEachTime;
                    let idSlice = joinIDList.slice(startCount, numberToTake + startCount);
                    const joinIDListConcat = idSlice.join(', ');
                    const joinIDWhere = `${FieldNames.featureFields.joinID} IN (${joinIDListConcat})`;
                    try {
                        const whereClause = `((${joinIDWhere}) AND (${FieldNames.featureFields.joinFeature} = '${joinFeature}'))`;
                        const queryObject = new Query({
                            where: whereClause,
                            outFields: outFields,
                        });
                        finalResults = await GeneralService.queryCall(url, queryObject);
                        if (finalResults && finalResults.length > 0) {
                            allFinalResults.push(finalResults);
                        }
                    } catch (e) {
                        throw e;
                    }
                    hasMoreRecords = joinIDList.length > startCount + numberToTakeEachTime;
                    startCount += numberToTakeEachTime;
                }
                const allFinalResultsFlattened =
                    allFinalResults != null && allFinalResults.length > 0
                        ? allFinalResults.flat()
                        : [];
                return allFinalResultsFlattened;
            } catch (e) {
                throw e;
            }
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
    getFeatureByFundingFieldsCountForPaged: async function (
        outFields,
        department,
        agency,
        region,
        unit,
        subunit,
        tribeFullName,
        fiscalYear,
        entityType,
        additionalWhereClause
    ) {
        const config = getAppConfig();
        const rootServiceUrl = getViewServiceUrl();
        const layerID = config.ifprsPolygonsView[config.env].layerId;
        const url = rootServiceUrl + '/' + layerID;
        const finalClause = getWhereClauseForFeaturesByFundingFields(
            department,
            agency,
            region,
            unit,
            subunit,
            tribeFullName,
            fiscalYear,
            entityType,
            additionalWhereClause
        );
        let countResults = 0;
        try {
            const queryObject = new Query({
                where: finalClause,
            });
            countResults = await GeneralService.queryCountCall(url, queryObject);
        } catch (e) {
            throw e;
        }
        return countResults;
    },

    getFeaturesByFundingFieldsPaged: async function (
        outFields,
        department,
        agency,
        region,
        unit,
        subunit,
        tribeFullName,
        fiscalYear,
        entityType,
        additionalWhereClause,
        page = 0,
        pageSize = 25
    ) {
        try {
            const config = getAppConfig();
            const rootServiceUrl = getViewServiceUrl();
            let allFinalResults = [];
            const layerID = config.fundingView[config.env].layerId; // Note: This does not pull geometries
            const url = rootServiceUrl + '/' + layerID;
            let outFieldStr = outFields ? outFields : '*';
            const finalClause = getWhereClauseForFeaturesByFundingFields(
                department,
                agency,
                region,
                unit,
                subunit,
                tribeFullName,
                fiscalYear,
                entityType,
                additionalWhereClause
            );

            const numberToTakeEachTime = pageSize;
            let startCount = page * pageSize;
            let finalResults = '';
            try {
                const queryObject = new Query({
                    where: finalClause,
                    outFields: outFieldStr,
                    orderByFields: ['OBJECTID ASC'],
                    start: startCount,
                    num: numberToTakeEachTime,
                });
                finalResults = await GeneralService.queryCall(url, queryObject);
                if (finalResults && finalResults.length > 0) {
                    allFinalResults.push(finalResults);
                }
            } catch (e) {
                throw e;
            }
            const allFinalResultsFlattened =
                allFinalResults != null && allFinalResults.length > 0 ? allFinalResults.flat() : [];
            return allFinalResultsFlattened;
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
    addFeature: function (attributes) {
        try {
            const config = getAppConfig();
            const rootServiceUrl = getAttributeServiceUrl();
            const nifcToken = getNifcToken();
            let featureUrl = `${rootServiceUrl}/${config.actionAttributes[config.env].layerId}/addFeatures`;

            return new Promise((resolve, reject) => {
                const feature = {
                    attributes: attributes,
                };

                new Request()
                    .makeRequest({
                        url: featureUrl,
                        method: 'post',
                        data: {
                            f: 'json',
                            token: nifcToken,
                            features: JSON.stringify([feature]),
                        },
                    })
                    .then(
                        (resp) => {
                            resolve(resp);
                        },
                        (error) => {
                            reject(error);
                        }
                    );
            });
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
    copyFeatureAttributes: function (attributes) {
        try {
            const config = getAppConfig();
            const rootServiceUrl = getAttributeServiceUrl();
            const nifcToken = getNifcToken();
            const featureUrl = `${rootServiceUrl}/${config.actionAttributes[config.env].layerId}/addFeatures`;
            return new Promise((resolve, reject) => {
                const feature = {
                    attributes: attributes,
                };
                new Request()
                    .makeRequest({
                        url: featureUrl,
                        method: 'post',
                        data: {
                            f: 'json',
                            token: nifcToken,
                            features: JSON.stringify([feature]),
                        },
                    })
                    .then(
                        (resp) => {
                            resolve(resp);
                        },
                        (error) => {
                            reject(error);
                        }
                    );
            });
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
    updateFeatureAttributes: function (allFeatures, featureUrlOverride = null) {
        try {
            const config = getAppConfig();
            const rootServiceUrl = getAttributeServiceUrl();
            const nifcToken = getNifcToken();
            let featureUrl = `${rootServiceUrl}/${config.actionAttributes[config.env].layerId}/updateFeatures`;
            if (featureUrlOverride !== null && typeof featureUrlOverride === 'string') {
                featureUrl = `${featureUrlOverride}/updateFeatures`;
            }
            return new Promise((resolve, reject) => {
                let adjustedFeatures = [];
                allFeatures.forEach((feature) => {
                    const newFeature = {
                        attributes: feature,
                    };
                    adjustedFeatures.push(newFeature);
                });
                new Request()
                    .makeRequest({
                        url: featureUrl,
                        method: 'post',
                        data: {
                            f: 'json',
                            token: nifcToken,
                            features: JSON.stringify(adjustedFeatures),
                        },
                    })
                    .then(
                        (resp) => {
                            resolve(resp);
                        },
                        (error) => {
                            reject(error);
                        }
                    );
            });
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
    updateMultipleFeaturesAttributes: async function (itemsForUpdating) {
        try {
            const config = getAppConfig();
            const rootServiceUrl = getAttributeServiceUrl();
            const nifcToken = getNifcToken();
            let allFinalResults = [];

            const layer = `${rootServiceUrl}/${config.actionAttributes[config.env].layerId}/updateFeatures`;

            let adjustedFeatures = [];
            itemsForUpdating.forEach((feature) => {
                const newFeature = {
                    attributes: feature,
                };
                adjustedFeatures.push(newFeature);
            });
            const numberToTakeEachTime = 500;
            let startCount = 0;
            let hasMoreRecords = true;
            let finalResults = '';
            while (hasMoreRecords) {
                const numberToTake =
                    startCount + numberToTakeEachTime > adjustedFeatures.length
                        ? adjustedFeatures.length - startCount
                        : numberToTakeEachTime;
                let numbersToRun = adjustedFeatures.slice(startCount, numberToTake + startCount);
                try {
                    finalResults = await GeneralService.updateCallForMultiples(
                        layer,
                        nifcToken,
                        numbersToRun
                    );
                    if (finalResults && finalResults.updateResults) {
                        allFinalResults.push(finalResults.updateResults);
                    }
                } catch (e) {
                    numbersToRun.forEach((item) => {
                        let itemAttr = item;
                        if (item.attributes) {
                            itemAttr = item.attributes;
                        }
                        const newItem = {
                            objectId: itemAttr[FieldNames.featureFields.entityID],
                            success: false,
                        };
                        allFinalResults.push(newItem);
                    });
                }
                hasMoreRecords = adjustedFeatures.length > startCount + numberToTakeEachTime;
                startCount += numberToTakeEachTime;
            }
            let failedItems = [];
            let successfulItems = [];
            if (!hasMoreRecords) {
                allFinalResults.flat().forEach((result) => {
                    if (result) {
                        if (!result.success) {
                            failedItems.push(result.objectId);
                        } else {
                            successfulItems.push(result.objectId);
                        }
                    }
                });
            }

            const allItemResults = {
                successful: successfulItems,
                failures: failedItems,
            };
            return allItemResults;
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
    updateFeature: function (feature) {
        try {
            // Handle data identifiers if this came from one of the geometry views
            let editableAttributesObj = _.cloneDeep(feature.attributes ?? feature);
            const config = getAppConfig();
            const rootServiceUrl = getAttributeServiceUrl();
            const nifcToken = getNifcToken();
            let featureUrl = `${rootServiceUrl}/${config.actionAttributes[config.env].layerId}/updateFeatures`;
            return new Promise((resolve, reject) => {
                const feature = {
                    attributes: editableAttributesObj,
                };
                new Request()
                    .makeRequest({
                        url: featureUrl,
                        method: 'post',
                        data: {
                            f: 'json',
                            token: nifcToken,
                            features: JSON.stringify([feature]),
                        },
                    })
                    .then(
                        (resp) => {
                            resolve(resp);
                        },
                        (error) => {
                            reject(error);
                        }
                    );
            });
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
    // NOTE: Originally planned on using a single function for add and updates and the applyEdits endpoint
    // however you cannot add and update features if you are using globalIds, it expects features in adds to already have a globalID
    addFeatures: function (serviceUrl, layerId, addFeatures = [], useGlobalIds = false) {
        // Handle data identifiers if this came from one of the geometry views
        let featureUrl = `${serviceUrl}/${layerId}/addFeatures`;

        return esriRequest(featureUrl, {
            method: 'post',
            responseType: 'json',
            query: {
                f: 'json',
                useGlobalIds: useGlobalIds ?? false,
                features: JSON.stringify(addFeatures),
            },
        });
    },
    updateFeatures: function (
        serviceUrl,
        layerId,
        updateFeatures = [],

        useGlobalIds = false
    ) {
        // Handle data identifiers if this came from one of the geometry views
        let featureUrl = `${serviceUrl}/${layerId}/updateFeatures`;

        return esriRequest(featureUrl, {
            method: 'post',
            responseType: 'json',
            query: {
                f: 'json',
                useGlobalIds: useGlobalIds ?? false,
                features: JSON.stringify(updateFeatures),
            },
        });
    },
    deleteFeatures: function (serviceUrl, layerId, deleteFeatureIds = [], useGlobalIds = false) {
        // Handle data identifiers if this came from one of the geometry views
        let featureUrl = `${serviceUrl}/${layerId}/deleteFeatures`;

        return esriRequest(featureUrl, {
            method: 'post',
            responseType: 'json',
            query: {
                f: 'json',
                useGlobalIds: useGlobalIds ?? false,
                [useGlobalIds ? 'globalIds' : 'objectIds']: JSON.stringify(deleteFeatureIds)
            },
        })
    },
    updateMultipleFeatures: async function (itemsForUpdating) {
        try {
            const config = getAppConfig();
            const rootServiceUrl = getAttributeServiceUrl();
            const nifcToken = getNifcToken();
            let allFinalResults = [];

            const featureUrl = `${rootServiceUrl}/${config.actionAttributes[config.env].layerId}/updateFeatures`;
            let adjustedFeatures = [];
            itemsForUpdating.forEach((feature) => {
                const newFeature = {
                    attributes: feature.attributes ? feature.attributes : feature,
                };
                adjustedFeatures.push(newFeature);
            });
            const numberToTakeEachTime = 100;
            let startCount = 0;
            let hasMoreRecords = true;
            let finalResults = '';
            while (hasMoreRecords) {
                const numberToTake =
                    startCount + numberToTakeEachTime > adjustedFeatures.length
                        ? adjustedFeatures.length - startCount
                        : numberToTakeEachTime;
                let numbersToRun = adjustedFeatures.slice(startCount, numberToTake + startCount);
                try {
                    finalResults = await GeneralService.updateCallForMultiples(
                        featureUrl,
                        nifcToken,
                        numbersToRun
                    );
                    if (finalResults && finalResults.updateResults) {
                        allFinalResults.push(finalResults.updateResults);
                    }
                } catch (e) {
                    numbersToRun.forEach((item) => {
                        let itemAttr = item;
                        if (item.attributes) {
                            itemAttr = item.attributes;
                        }
                        const newItem = {
                            objectId: itemAttr[FieldNames.featureFields.objectID],
                            success: false,
                        };
                        allFinalResults.push(newItem);
                    });
                }
                hasMoreRecords = adjustedFeatures.length > startCount + numberToTakeEachTime;
                startCount += numberToTakeEachTime;
            }
            let failedItems = [];
            let successfulItems = [];
            if (!hasMoreRecords) {
                allFinalResults.flat().forEach((result) => {
                    if (result) {
                        if (!result.success) {
                            failedItems.push(result.objectId);
                        } else {
                            successfulItems.push(result.objectId);
                        }
                    }
                });
            }

            const allItemResults = {
                successful: successfulItems,
                failures: failedItems,
            };
            return allItemResults;
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject(e);
            });
        }
    },
};

export default FeatureService;